import { useState, useEffect, useCallback } from 'react';
import { Spin, notification, Modal } from 'antd';
import {
  AimOutlined, BookOutlined, BulbOutlined, CameraOutlined, EnvironmentOutlined,
  GlobalOutlined, HeartOutlined, InfoCircleOutlined, MessageOutlined, PhoneOutlined, QuestionCircleOutlined,
  ReadOutlined, StarOutlined, TeamOutlined, UserOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../config';
import { M, G } from '../shared/constants';
import { authHeader, useLang, tl, toTitle, ssGet, ssSet } from '../shared/utils';
import { ProfilePhotoUpload } from '../components/CropModal';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const COMPLEXION_LABEL = { very_fair:'Very Fair', fair:'Fair', wheatish:'Wheatish', dark:'Dark' };
const FAMILY_TYPE_LABEL = { joint:t=>(t==='en'?'Joint Family':'संयुक्त परिवार'), nuclear:t=>(t==='en'?'Nuclear Family':'एकल परिवार') };
const MARITAL_LABEL = { never_married:'Never Married', divorced:'Divorced', widowed:'Widowed' };
const FAMILY_VAL_LABEL = { traditional:'Traditional', moderate:'Moderate', liberal:'Liberal' };

const iStyle = { width:'100%', padding:'9px 12px', border:'1.5px solid #e5e7eb', borderRadius:8, fontSize:14, outline:'none', boxSizing:'border-box', background:'#fff' };
const lStyle = { fontSize:12, fontWeight:700, color:'#374151', marginBottom:4, display:'block' };

// eslint-disable-next-line no-unused-vars
export function VivahParichayTab({ profile, onOpenChat }) {
  const { t } = useLang(); // eslint-disable-line no-unused-vars
  const [view,       setView]       = useState('browse');
  const [profiles,   setProfiles]   = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [myProfile,  setMyProfile]  = useState(null);
  const [received,   setReceived]   = useState([]);
  const [sent,       setSent]       = useState([]);
  const [filter,     setFilter]     = useState({ gender:'', nri:'' });
  const [viewProfile,setViewProfile]= useState(null);
  const [interestMsg,setInterestMsg]= useState('');
  const [sending,    setSending]    = useState(false);
  const [responding, setResponding] = useState(null);
  const [submitting,     setSubmitting]     = useState(false);
  const [form,           setForm]           = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const myGotra = profile?.gotra || '';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page });
      if (filter.gender) p.set('gender', filter.gender);
      if (filter.nri)    p.set('nri',    filter.nri);
      if (myGotra)       p.set('gotra_exclude', myGotra);

      const [bRes, mpRes, recRes, sentRes] = await Promise.all([
        axios.get(`${baseUrl}/member/vivah?${p}`,                   { headers: authHeader() }),
        axios.get(`${baseUrl}/member/vivah/my-profile`,             { headers: authHeader() }),
        axios.get(`${baseUrl}/member/vivah/interests/received`,     { headers: authHeader() }),
        axios.get(`${baseUrl}/member/vivah/interests/sent`,         { headers: authHeader() }),
      ]);
      if (bRes.data.status)   { setProfiles(bRes.data.result || []); setTotal(bRes.data.total || 0); }
      if (mpRes.data.status)  setMyProfile(mpRes.data.result);
      if (recRes.data.status) setReceived(recRes.data.result || []);
      if (sentRes.data.status)setSent(sentRes.data.result || []);
    } catch {}
    finally { setLoading(false); }
  }, [page, filter, myGotra]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const initForm = (existing) => {
    if (existing) {
      // Editing existing profile — normalize dob from ISO to YYYY-MM-DD for <input type="date">
      setForm({
        ...existing,
        dob: existing.dob ? existing.dob.split('T')[0] : '',
        height_cm: existing.height_cm ?? '',
        pref_age_min: existing.pref_age_min ?? '',
        pref_age_max: existing.pref_age_max ?? '',
        is_nri: existing.is_nri ?? 0,
        photo_visible: existing.photo_visible ?? 0,
        pref_nri_ok: existing.pref_nri_ok ?? 1,
      });
    } else {
      // New profile — pre-fill from portal profile
      setForm({
        gender: profile?.gender || 'male',
        display_name: (profile?.name_hn || profile?.name_eng || profile?.name || '').split(' ')[0] + ' ' + ((profile?.name_hn || profile?.name_eng || profile?.name || '').split(' ').slice(-1)[0]?.[0] || '') + '.',
        dob: profile?.dob?.split('T')[0] || '',
        height_cm: '', complexion: '', marital_status: 'never_married',
        education: profile?.education || '', education_field: '', occupation: profile?.occupation || '',
        company: '', income_range: '',
        current_city: profile?.temp_address?.city || '', current_state: profile?.temp_address?.state || '',
        current_country: profile?.temp_address?.country || 'India', is_nri: profile?.temp_address?.country && profile.temp_address.country.toLowerCase() !== 'india' ? 1 : 0,
        gotra: profile?.gotra || '', kuldevi: profile?.kuldevi || '',
        native_village: profile?.perm_address?.village || '', native_district: profile?.perm_address?.district || '',
        father_name: profile?.father_name || '', father_occupation: '', mother_kuldevi: '',
        siblings: '', family_type: '', family_values: '',
        about: '', hobbies: '', photo: profile?.photo || '', photo_visible: 0,
        pref_age_min: '', pref_age_max: '', pref_education: '', pref_city: '', pref_nri_ok: 1,
      });
    }
    setView('my_profile_form');
  };

  const handleVivahPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const res = await axios.post(`${baseUrl}/member/vivah/upload-photo`, fd, {
        headers: authHeader(),
      });
      if (res.data.status) {
        setForm(f => ({ ...f, photo: res.data.photo_url }));
        notification.success({ message: t('Photo upload हो गई!', 'Photo uploaded!'), placement: 'top' });
      } else notification.error({ message: res.data.message || 'Upload failed' });
    } catch { notification.error({ message: t('Photo upload failed', 'Photo upload failed') }); }
    finally { setUploadingPhoto(false); e.target.value = ''; }
  };

  const saveProfile = async () => {
    const warn = (msg) => { notification.warning({ message: msg, placement: 'top' }); };
    if (!form.display_name?.trim()) { warn(t('नाम जरूरी है','Name is required')); return; }
    if (form.display_name.trim().length < 3) { warn(t('नाम कम से कम 3 अक्षर होना चाहिए','Name must be at least 3 characters')); return; }
    if (!form.gender) { warn(t('लिंग (Gender) जरूरी है','Gender is required')); return; }
    if (!form.dob) { warn(t('जन्म तिथि जरूरी है','Date of birth is required')); return; }
    const age = Math.floor((Date.now() - new Date(form.dob)) / (365.25 * 24 * 3600 * 1000));
    if (age < 18) { warn(t('आयु 18 वर्ष से कम नहीं हो सकती','Age must be at least 18 years')); return; }
    if (age > 70) { warn(t('कृपया सही जन्म तिथि भरें','Please enter a valid date of birth')); return; }
    if (form.height_cm && (Number(form.height_cm) < 100 || Number(form.height_cm) > 250)) {
      warn(t('ऊँचाई 100–250 cm के बीच होनी चाहिए','Height must be between 100 and 250 cm')); return;
    }
    if (form.pref_age_min && Number(form.pref_age_min) < 18) { warn(t('पसंदीदा न्यूनतम आयु 18+ होनी चाहिए','Preferred min age must be 18+')); return; }
    if (form.pref_age_min && form.pref_age_max && Number(form.pref_age_min) > Number(form.pref_age_max)) {
      warn(t('न्यूनतम आयु अधिकतम से कम होनी चाहिए','Min age must be less than max age')); return;
    }
    if (!form.gotra?.trim()) { warn(t('गोत्र जरूरी है','Gotra is required')); return; }
    if (!form.native_village?.trim()) { warn(t('मूल गाँव जरूरी है','Native village is required')); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${baseUrl}/member/vivah/profile`, form, { headers: authHeader() });
      if (res.data.status) {
        notification.success({ message: res.data.message, placement: 'top' });
        setView('browse'); fetchData();
      } else notification.error({ message: res.data.message, placement: 'top' });
    } catch (err) { notification.error({ message: err?.response?.data?.message || 'Error', placement: 'top' }); }
    finally { setSubmitting(false); }
  };

  const sendInterest = async (profileId) => {
    setSending(true);
    try {
      const res = await axios.post(`${baseUrl}/member/vivah/${profileId}/interest`, { message: interestMsg }, { headers: authHeader() });
      if (res.data.status) {
        notification.success({ message: t('रुचि भेजी गई! ✅','Interest sent! ✅'), placement: 'top' });
        setViewProfile(null); setInterestMsg(''); fetchData();
      } else notification.error({ message: res.data.message, placement: 'top' });
    } catch (err) { notification.error({ message: err?.response?.data?.message || 'Same gotra restriction or already sent', placement: 'top' }); }
    finally { setSending(false); }
  };

  const respond = async (interestId, action) => {
    setResponding(interestId);
    try {
      const res = await axios.patch(`${baseUrl}/member/vivah/interests/${interestId}`, { action }, { headers: authHeader() });
      if (res.data.status) { notification.success({ message: res.data.message, placement: 'top' }); fetchData(); }
    } catch (err) { notification.error({ message: err?.response?.data?.message || 'Error', placement: 'top' }); }
    finally { setResponding(null); }
  };

  const updateStatus = async (status) => {
    try {
      await axios.patch(`${baseUrl}/member/vivah/my-profile/status`, { status }, { headers: authHeader() });
      notification.success({ message: t('Status updated','Status updated'), placement: 'top' });
      fetchData();
    } catch {}
  };

  // Blurred photo placeholder — shown when profile has photo but photo_visible=0
  const BlurPhoto = ({ size = 56, radius = '50%', showLabel = false }) => (
    <div style={{
      width: size, height: size, borderRadius: radius,
      background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 40%, #f9a8d4 70%, #fce7f3 100%)',
      border: `2px solid #f472b6`,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      position: 'relative', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Bokeh circles to simulate blur */}
      <div style={{ position:'absolute', inset:0, opacity:0.4,
        background:'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.6) 0%, transparent 50%), radial-gradient(circle at 70% 75%, rgba(219,39,119,0.25) 0%, transparent 55%)'
      }} />
      {/* Silhouette */}
      <UserOutlined style={{ fontSize: size * 0.38, color: '#be185d', opacity: 0.65, zIndex: 1 }} />
      {/* Lock badge */}
      <div style={{
        position: 'absolute', bottom: size > 70 ? 8 : 2, right: size > 70 ? 8 : 2,
        width: size > 70 ? 22 : 16, height: size > 70 ? 22 : 16,
        borderRadius: '50%', background: '#fff', border: '1.5px solid #f472b6',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size > 70 ? 11 : 8, zIndex: 2,
      }}>🔒</div>
      {/* Label under large version */}
      {showLabel && (
        <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'rgba(190,24,93,0.75)', padding:'4px 2px', textAlign:'center' }}>
          <div style={{ fontSize: 9, fontWeight:800, color:'#fff', lineHeight:1.2 }}>रुचि भेजें</div>
          <div style={{ fontSize: 8, color:'rgba(255,255,255,0.8)', lineHeight:1.2 }}>फोटो unlock होगी</div>
        </div>
      )}
    </div>
  );

  const VIVAH_TABS = [
    { k:'browse',   l:t('प्रोफाइल देखें','Browse Profiles'), cnt: total > 0 ? total : null },
    { k:'received', l:t('प्राप्त रुचि','Received'),          cnt: received.filter(r=>r.status==='pending').length || null },
    { k:'sent',     l:t('भेजी रुचि','Sent'),                 cnt: sent.length > 0 ? sent.length : null },
    { k:'my_profile',l:t('मेरी Profile','My Profile'),       cnt: null },
  ];

  const ProfileCard = ({ p: pr, compact }) => {
    const isSameGotra = myGotra && pr.gotra && myGotra.toLowerCase() === pr.gotra?.toLowerCase();
    const alreadySent = pr.my_interest;
    return (
      <div style={{ background:'#fff', borderRadius:13, border:`1.5px solid ${isSameGotra ? '#fca5a5' : '#e5e7eb'}`, padding:'13px', marginBottom:10, boxShadow:'0 1px 6px rgba(0,0,0,0.06)' }}>
        {isSameGotra && (
          <div style={{ background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:8, padding:'5px 10px', marginBottom:8, fontSize:11, color:'#991b1b', fontWeight:700 }}>
            ⚠️ {t('समान गोत्र — परंपरा अनुसार रुचि नहीं भेज सकते','Same Gotra — Interest not allowed by tradition')}
          </div>
        )}
        <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
          {/* Photo */}
          <div style={{ flexShrink:0 }}>
            {pr.photo ? (
              <img src={pr.photo} alt={pr.display_name} style={{ width:56, height:56, borderRadius:'50%', objectFit:'cover', objectPosition:'top', border:`2px solid ${M}` }} />
            ) : pr.has_photo ? (
              <BlurPhoto size={56} />
            ) : (
              <div style={{ width:56, height:56, borderRadius:'50%', background:`${M}18`, border:`2px solid ${M}30`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:900, color:M }}>
                {pr.display_name?.[0]?.toUpperCase() || '?'}
              </div>
            )}
            {pr.is_nri === 1 && <div style={{ textAlign:'center', fontSize:9, fontWeight:700, color:'#92400e', background:'#fef3c7', borderRadius:10, padding:'1px 4px', marginTop:3 }}>NRI</div>}
          </div>

          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
              <div style={{ fontSize:14, fontWeight:900, color:'#111827' }}>{pr.display_name}</div>
              {pr.age && <span style={{ fontSize:11, color:'#6b7280' }}>· {pr.age} yrs</span>}
              {pr.height_cm && <span style={{ fontSize:11, color:'#6b7280' }}>· {Math.floor(pr.height_cm/30.48)}'{Math.round((pr.height_cm%30.48)/2.54)}"</span>}
            </div>

            <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginTop:6 }}>
              {pr.education && <span style={{ background:'#f3f4f6', color:'#374151', borderRadius:20, padding:'2px 8px', fontSize:10 }}>{pr.education}{pr.education_field?` · ${pr.education_field}`:''}</span>}
              {pr.occupation && <span style={{ background:'#f3f4f6', color:'#374151', borderRadius:20, padding:'2px 8px', fontSize:10 }}>{pr.occupation}</span>}
              {pr.income_range && <span style={{ background:'#f0fdf4', color:'#15803d', borderRadius:20, padding:'2px 8px', fontSize:10, fontWeight:600 }}>₹ {pr.income_range}</span>}
              {pr.current_city && <span style={{ background:'#f3f4f6', color:'#374151', borderRadius:20, padding:'2px 8px', fontSize:10 }}><EnvironmentOutlined style={{ fontSize:9 }}/> {toTitle(pr.current_city)}{pr.current_state?`, ${toTitle(pr.current_state)}`:''}</span>}
            </div>

            <div style={{ display:'flex', gap:5, marginTop:6, flexWrap:'wrap' }}>
              {pr.gotra && <span style={{ background:`${M}10`, color:M, borderRadius:20, padding:'2px 8px', fontSize:10, fontWeight:700 }}>{t('गोत्र','Gotra')}: {pr.gotra}</span>}
              {pr.kuldevi && <span style={{ background:`${M}08`, color:M, borderRadius:20, padding:'2px 8px', fontSize:10 }}>{t('कुलदेवी','Kuldevi')}: {pr.kuldevi}</span>}
              {pr.native_village && <span style={{ background:'#f9fafb', color:'#6b7280', borderRadius:20, padding:'2px 8px', fontSize:10 }}>{pr.native_village}</span>}
            </div>

            {!compact && pr.about && <div style={{ fontSize:11, color:'#6b7280', marginTop:7, lineHeight:1.6 }}>{pr.about.slice(0, 120)}{pr.about.length > 120 ? '…' : ''}</div>}

            {!isSameGotra && (
              <div style={{ display:'flex', gap:8, marginTop:10 }}>
                <button onClick={() => { setViewProfile(pr); setInterestMsg(''); }} style={{ flex:1, background:'#f9fafb', border:`1px solid ${M}30`, borderRadius:9, padding:'7px 0', fontSize:11, fontWeight:700, color:M, cursor:'pointer' }}>
                  {t('पूरा देखें','View Full')}
                </button>
                {!alreadySent ? (
                  <button onClick={() => { setViewProfile(pr); setInterestMsg(''); }} style={{ flex:1.5, background:M, border:'none', borderRadius:9, padding:'7px 0', fontSize:11, fontWeight:800, color:'#fff', cursor:'pointer' }}>
                    {t('💍 रुचि भेजें','💍 Send Interest')}
                  </button>
                ) : (
                  <span style={{ flex:1.5, background: alreadySent==='accepted'?'#f0fdf4':alreadySent==='declined'?'#fef2f2':'#fffbeb', color: alreadySent==='accepted'?'#15803d':alreadySent==='declined'?'#991b1b':'#92400e', borderRadius:9, padding:'7px 10px', fontSize:11, fontWeight:700, textAlign:'center' }}>
                    {alreadySent==='accepted'?t('✅ Accepted','✅ Accepted'):alreadySent==='declined'?t('❌ Declined','❌ Declined'):t('⏳ Sent','⏳ Sent')}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{ background:`linear-gradient(135deg,${M},#b71c1c)`, borderRadius:14, padding:'14px 16px', marginBottom:14 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <HeartOutlined style={{ fontSize:22, color:'#fff' }}/>
          <div>
            <div style={{ fontSize:15, fontWeight:900, color:'#fff' }}>{t('विवाह परिचय','Vivah Parichay')}</div>
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.7)', marginTop:2 }}>
              {t('समाज के सदस्यों के लिए सुरक्षित विवाह परिचय मंच','Safe matrimonial platform for community members')}
            </div>
          </div>
        </div>
      </div>

      {/* Gotra privacy note */}
      {myGotra && (
        <div style={{ background:'#fff8f0', border:`1px solid ${M}25`, borderRadius:10, padding:'9px 13px', marginBottom:12, display:'flex', gap:8, alignItems:'flex-start' }}>
          <InfoCircleOutlined style={{ color:M, fontSize:14, marginTop:1, flexShrink:0 }}/>
          <div style={{ fontSize:11, color:'#374151', lineHeight:1.6 }}>
            {t(`आपका गोत्र "${myGotra}" है — समान गोत्र के profiles स्वतः छिपाए गए हैं।`,
               `Your gotra is "${myGotra}" — profiles with the same gotra are automatically hidden.`)}
          </div>
        </div>
      )}

      {/* Nav tabs */}
      <div style={{ display:'flex', borderBottom:'2px solid #f3f4f6', marginBottom:14, overflowX:'auto' }}>
        {VIVAH_TABS.map(tab => (
          <button key={tab.k} onClick={() => setView(tab.k)} style={{
            background:'none', border:'none', padding:'9px 12px', cursor:'pointer', whiteSpace:'nowrap',
            fontWeight: view===tab.k ? 800 : 500, fontSize:12,
            color: view===tab.k ? M : '#6b7280',
            borderBottom: view===tab.k ? `2.5px solid ${M}` : '2.5px solid transparent',
            display:'flex', alignItems:'center', gap:5,
          }}>
            {tab.l}
            {tab.cnt && <span style={{ background:`${M}18`, color:M, borderRadius:20, padding:'1px 6px', fontSize:10, fontWeight:800 }}>{tab.cnt}</span>}
          </button>
        ))}
      </div>

      {/* ── Browse ── */}
      {view === 'browse' && (
        <>
          {!myProfile && (
            <div style={{ background:'#fff8f0', border:`1.5px solid ${M}30`, borderRadius:12, padding:'12px 14px', marginBottom:14, display:'flex', alignItems:'center', gap:12 }}>
              <HeartOutlined style={{ color:M, fontSize:20, flexShrink:0 }}/>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, fontWeight:800, color:M }}>{t('रुचि भेजने के लिए अपनी Profile बनाएं','Create your profile to send interest')}</div>
                <div style={{ fontSize:10, color:'#6b7280', marginTop:2 }}>{t('Admin approval के बाद visible होगी।','Visible after admin approval.')}</div>
              </div>
              <button onClick={() => initForm(null)} style={{ background:M, color:'#fff', border:'none', borderRadius:9, padding:'8px 13px', fontSize:12, fontWeight:800, cursor:'pointer', flexShrink:0 }}>
                {t('बनाएं','Create')}
              </button>
            </div>
          )}

          {/* Filters */}
          <div style={{ display:'flex', gap:8, marginBottom:12 }}>
            <select value={filter.gender} onChange={e=>setFilter(f=>({...f,gender:e.target.value}))} style={{ ...iStyle, flex:1 }}>
              <option value="">{t('सभी (वर/वधू)','All (Bride & Groom)')}</option>
              <option value="male">{t('वर (लड़का)','Groom (Male)')}</option>
              <option value="female">{t('वधू (लड़की)','Bride (Female)')}</option>
            </select>
            <select value={filter.nri} onChange={e=>setFilter(f=>({...f,nri:e.target.value}))} style={{ ...iStyle, flex:1 }}>
              <option value="">{t('सभी','All')}</option>
              <option value="0">{t('भारत में','India')}</option>
              <option value="1">NRI</option>
            </select>
          </div>

          {loading && <div style={{ textAlign:'center', padding:40 }}><Spin /></div>}
          {!loading && profiles.length === 0 && (
            <div style={{ textAlign:'center', padding:48, color:'#9ca3af' }}>
              <HeartOutlined style={{ fontSize:36, display:'block', margin:'0 auto 10px', color:'#fca5a5' }}/>
              <div>{t('अभी कोई Profile उपलब्ध नहीं है।','No profiles available yet.')}</div>
              <div style={{ fontSize:11, marginTop:4 }}>{t('पहले Admin से approval लें।','Profiles appear after admin approval.')}</div>
            </div>
          )}
          {!loading && profiles.map(pr => <ProfileCard key={pr.id} p={pr} />)}

          {/* Pagination */}
          {total > 20 && (
            <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:14 }}>
              <button disabled={page===1} onClick={() => setPage(p=>p-1)} style={{ padding:'6px 14px', borderRadius:8, border:'1px solid #e5e7eb', background:page===1?'#f9fafb':'#fff', cursor:page===1?'default':'pointer', fontSize:12, fontWeight:600, color:page===1?'#9ca3af':'#374151' }}>← {t('पिछला','Prev')}</button>
              <span style={{ padding:'6px 12px', fontSize:12, color:'#6b7280' }}>{t('पृष्ठ','Page')} {page}</span>
              <button disabled={profiles.length < 20} onClick={() => setPage(p=>p+1)} style={{ padding:'6px 14px', borderRadius:8, border:'1px solid #e5e7eb', background:profiles.length<20?'#f9fafb':'#fff', cursor:profiles.length<20?'default':'pointer', fontSize:12, fontWeight:600, color:profiles.length<20?'#9ca3af':'#374151' }}>{t('अगला','Next')} →</button>
            </div>
          )}
        </>
      )}

      {/* ── Received Interests ── */}
      {view === 'received' && (
        <div>
          {loading && <div style={{ textAlign:'center', padding:32 }}><Spin /></div>}
          {!loading && received.length === 0 && <div style={{ textAlign:'center', padding:48, color:'#9ca3af' }}>{t('अभी कोई रुचि नहीं मिली।','No interests received yet.')}</div>}
          {!loading && received.map(r => (
            <div key={r.id} style={{ background:'#fff', borderRadius:12, border:`1.5px solid ${r.status==='accepted'?'#86efac':r.status==='declined'?'#fca5a5':'#e5e7eb'}`, padding:'13px 14px', marginBottom:10, boxShadow:'0 1px 5px rgba(0,0,0,0.05)' }}>
              <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                <div style={{ width:44, height:44, borderRadius:'50%', background:`${M}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:900, color:M, flexShrink:0 }}>
                  {r.from_name?.[0]?.toUpperCase()||'?'}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <div style={{ fontSize:13, fontWeight:800, color:'#111827' }}>{r.from_name}</div>
                    {r.from_age && <span style={{ fontSize:11, color:'#6b7280' }}>{r.from_age} yrs</span>}
                    <span style={{ fontSize: 9, background: r.status==='accepted'?'#f0fdf4':r.status==='declined'?'#fef2f2':'#fffbeb', color:r.status==='accepted'?'#15803d':r.status==='declined'?'#991b1b':'#92400e', borderRadius:20, padding:'1px 8px', fontWeight:800 }}>
                      {r.status==='accepted'?t('✅ Accepted','✅ Accepted'):r.status==='declined'?t('❌ Declined','❌ Declined'):t('⏳ Pending','⏳ Pending')}
                    </span>
                  </div>
                  <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginTop:5 }}>
                    {r.from_occ && <span style={{ background:'#f3f4f6', color:'#374151', borderRadius:20, padding:'2px 8px', fontSize:10 }}>{r.from_occ}</span>}
                    {r.from_city && <span style={{ background:'#f3f4f6', color:'#374151', borderRadius:20, padding:'2px 8px', fontSize:10 }}><EnvironmentOutlined style={{ fontSize:9 }}/> {toTitle(r.from_city)}</span>}
                    {r.from_gotra && <span style={{ background:`${M}10`, color:M, borderRadius:20, padding:'2px 8px', fontSize:10, fontWeight:700 }}>{t('गोत्र','Gotra')}: {r.from_gotra}</span>}
                  </div>
                  {r.message && <div style={{ fontSize:11, color:'#374151', marginTop:6, background:'#f9fafb', borderRadius:7, padding:'6px 9px', lineHeight:1.6 }}>"{r.message}"</div>}
                  {r.status === 'pending' && (
                    <div style={{ display:'flex', gap:8, marginTop:10 }}>
                      <button disabled={!!responding} onClick={() => respond(r.id, 'decline')} style={{ flex:1, background:'#fff', border:'1.5px solid #fca5a5', borderRadius:9, padding:'8px 0', fontSize:12, fontWeight:700, color:'#dc2626', cursor:'pointer' }}>
                        {t('❌ Decline','❌ Decline')}
                      </button>
                      <button disabled={!!responding} onClick={() => respond(r.id, 'accept')} style={{ flex:1.5, background:M, border:'none', borderRadius:9, padding:'8px 0', fontSize:12, fontWeight:800, color:'#fff', cursor:'pointer' }}>
                        {responding===r.id ? <Spin size="small"/> : t('✅ Accept करें','✅ Accept')}
                      </button>
                    </div>
                  )}
                  {r.status === 'accepted' && r.chat_user_id && onOpenChat && (
                    <div style={{ marginTop:10 }}>
                      <button onClick={() => onOpenChat(r.chat_user_id)} style={{ display:'inline-flex', alignItems:'center', gap:6, background:'#1d4ed8', color:'#fff', border:'none', borderRadius:9, padding:'8px 14px', fontSize:12, fontWeight:800, cursor:'pointer' }}>
                        <MessageOutlined /> {t('Chat करें','Chat Now')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Sent Interests ── */}
      {view === 'sent' && (
        <div>
          {loading && <div style={{ textAlign:'center', padding:32 }}><Spin /></div>}
          {!loading && sent.length === 0 && <div style={{ textAlign:'center', padding:48, color:'#9ca3af' }}>{t('आपने अभी कोई रुचि नहीं भेजी।','You have not sent any interest yet.')}</div>}
          {!loading && sent.map(s => (
            <div key={s.id} style={{ background:'#fff', borderRadius:12, border:`1.5px solid ${s.status==='accepted'?'#86efac':s.status==='declined'?'#fca5a5':'#e5e7eb'}`, padding:'13px 14px', marginBottom:10, boxShadow:'0 1px 5px rgba(0,0,0,0.05)' }}>
              <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                <div style={{ width:44, height:44, borderRadius:'50%', background:`${M}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:900, color:M, flexShrink:0 }}>
                  {s.to_photo ? <img src={s.to_photo} alt="" style={{ width:44, height:44, borderRadius:'50%', objectFit:'cover', objectPosition:'top' }}/> : s.to_name?.[0]?.toUpperCase()||'?'}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <div style={{ fontSize:13, fontWeight:800, color:'#111827' }}>{s.to_name}</div>
                    {s.to_age && <span style={{ fontSize:11, color:'#6b7280' }}>{s.to_age} yrs</span>}
                    <span style={{ fontSize:9, background: s.status==='accepted'?'#f0fdf4':s.status==='declined'?'#fef2f2':'#fffbeb', color:s.status==='accepted'?'#15803d':s.status==='declined'?'#991b1b':'#92400e', borderRadius:20, padding:'1px 8px', fontWeight:800 }}>
                      {s.status==='accepted'?t('✅ Accepted','✅ Accepted'):s.status==='declined'?t('❌ Declined','❌ Declined'):t('⏳ Pending','⏳ Pending')}
                    </span>
                  </div>
                  <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginTop:5 }}>
                    {s.to_occ && <span style={{ background:'#f3f4f6', color:'#374151', borderRadius:20, padding:'2px 8px', fontSize:10 }}>{s.to_occ}</span>}
                    {s.to_city && <span style={{ background:'#f3f4f6', color:'#374151', borderRadius:20, padding:'2px 8px', fontSize:10 }}><EnvironmentOutlined style={{ fontSize:9 }}/> {toTitle(s.to_city)}</span>}
                    {s.to_gotra && <span style={{ background:`${M}10`, color:M, borderRadius:20, padding:'2px 8px', fontSize:10, fontWeight:700 }}>{t('गोत्र','Gotra')}: {s.to_gotra}</span>}
                  </div>
                  {/* Show contact + chat if accepted */}
                  {s.status === 'accepted' && (
                    <div style={{ display:'flex', gap:8, marginTop:8, flexWrap:'wrap' }}>
                      {s.contact_phone && (
                        <a href={`tel:${s.contact_phone}`} style={{ display:'inline-flex', alignItems:'center', gap:5, background:M, color:'#fff', borderRadius:8, padding:'6px 13px', textDecoration:'none', fontSize:12, fontWeight:800 }}>
                          <PhoneOutlined /> {s.contact_phone}
                        </a>
                      )}
                      {s.chat_user_id && onOpenChat && (
                        <button onClick={() => onOpenChat(s.chat_user_id)} style={{ display:'inline-flex', alignItems:'center', gap:5, background:'#1d4ed8', color:'#fff', border:'none', borderRadius:8, padding:'6px 13px', fontSize:12, fontWeight:800, cursor:'pointer' }}>
                          <MessageOutlined /> {t('Chat करें','Chat')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── My Profile summary ── */}
      {view === 'my_profile' && (
        <div>
          {!myProfile ? (
            <div style={{ textAlign:'center', padding:48 }}>
              <HeartOutlined style={{ fontSize:40, display:'block', margin:'0 auto 12px', color:'#fca5a5' }}/>
              <div style={{ fontSize:14, fontWeight:700, color:'#374151', marginBottom:8 }}>{t('आपकी कोई Vivah Profile नहीं है।','You do not have a matrimonial profile yet.')}</div>
              <div style={{ fontSize:11, color:'#9ca3af', marginBottom:16 }}>{t('Profile बनाएं और Admin approval के बाद browse में दिखेगी।','Create a profile — visible after admin approval.')}</div>
              <button onClick={() => initForm(null)} style={{ background:M, color:'#fff', border:'none', borderRadius:10, padding:'11px 24px', fontSize:13, fontWeight:800, cursor:'pointer' }}>
                💍 {t('Profile बनाएं','Create Profile')}
              </button>
            </div>
          ) : (
            <div>
              {/* Status card */}
              <div style={{ background: myProfile.status==='active'&&myProfile.is_approved?'#f0fdf4':myProfile.status==='pending'||!myProfile.is_approved?'#fffbeb':'#fef2f2', border:`1.5px solid ${myProfile.status==='active'&&myProfile.is_approved?'#86efac':myProfile.status==='pending'?'#fde68a':'#fca5a5'}`, borderRadius:12, padding:'12px 14px', marginBottom:14 }}>
                <div style={{ fontSize:13, fontWeight:800, color:'#111827', marginBottom:4 }}>
                  {myProfile.status==='active'&&myProfile.is_approved ? t('✅ Profile Active है','✅ Profile is Active') :
                   myProfile.status==='pending'||!myProfile.is_approved ? t('⏳ Admin Approval pending है','⏳ Pending admin approval') :
                   myProfile.status==='married' ? t('💍 Married — Profile Hidden','💍 Married — Profile Hidden') :
                   myProfile.status==='paused' ? t('⏸ Profile Paused है','⏸ Profile is Paused') : myProfile.status}
                </div>
                {myProfile.admin_note && <div style={{ fontSize:11, color:'#6b7280', marginTop:4 }}><strong>Admin Note:</strong> {myProfile.admin_note}</div>}
                <div style={{ display:'flex', gap:8, marginTop:10, flexWrap:'wrap' }}>
                  <button onClick={() => initForm(myProfile)} style={{ background:M, color:'#fff', border:'none', borderRadius:8, padding:'7px 13px', fontSize:12, fontWeight:700, cursor:'pointer' }}>{t('✏️ Edit करें','✏️ Edit')}</button>
                  {myProfile.status !== 'married' && <button onClick={() => { if (window.confirm(t('क्या आप वाकई Married Mark करना चाहते हैं? यह आपकी profile को hide कर देगा।','Are you sure you want to mark as Married? This will hide your profile.'))) updateStatus('married'); }} style={{ background:'#f0fdf4', color:'#15803d', border:'1px solid #86efac', borderRadius:8, padding:'7px 13px', fontSize:12, fontWeight:700, cursor:'pointer' }}>💍 {t('Married Mark करें','Mark as Married')}</button>}
                  {myProfile.status === 'active' && <button onClick={() => updateStatus('paused')} style={{ background:'#fffbeb', color:'#92400e', border:'1px solid #fde68a', borderRadius:8, padding:'7px 13px', fontSize:12, fontWeight:700, cursor:'pointer' }}>⏸ {t('Pause करें','Pause')}</button>}
                  {myProfile.status === 'paused' && <button onClick={() => updateStatus('active')} style={{ background:'#eff6ff', color:'#1d4ed8', border:'1px solid #bfdbfe', borderRadius:8, padding:'7px 13px', fontSize:12, fontWeight:700, cursor:'pointer' }}>▶ {t('Resume करें','Resume')}</button>}
                </div>
              </div>

              {/* Profile preview */}
              <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e5e7eb', padding:'14px', marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:700, color:M, textTransform:'uppercase', letterSpacing:0.4, marginBottom:12 }}>{t('आपकी Profile का Preview','Your Profile Preview')}</div>
                {[
                  [t('नाम','Display Name'), myProfile.display_name],
                  [t('आयु','Age'), myProfile.age ? `${myProfile.age} ${t('वर्ष','years')}` : null],
                  [t('लिंग','Gender'), myProfile.gender==='male'?t('वर','Male'):t('वधू','Female')],
                  [t('शिक्षा','Education'), [myProfile.education, myProfile.education_field].filter(Boolean).join(' · ')],
                  [t('व्यवसाय','Occupation'), myProfile.occupation],
                  [t('आय','Income'), myProfile.income_range],
                  [t('वर्तमान शहर','Current City'), [myProfile.current_city, myProfile.current_state, myProfile.is_nri?myProfile.current_country:null].filter(Boolean).map(toTitle).join(', ')],
                  [t('गोत्र','Gotra'), myProfile.gotra],
                  [t('कुलदेवी','Kuldevi'), myProfile.kuldevi],
                  [t('मूल गाँव','Native Village'), [myProfile.native_village, myProfile.native_district].filter(Boolean).join(', ')],
                  [t('परिवार','Family'), myProfile.family_type ? (myProfile.family_type==='joint'?t('संयुक्त परिवार','Joint Family'):t('एकल परिवार','Nuclear Family')) : null],
                ].filter(([, v]) => v).map(([l, v]) => (
                  <div key={l} style={{ display:'flex', gap:10, padding:'6px 0', borderBottom:'1px solid #f9fafb' }}>
                    <span style={{ fontSize:11, color:'#9ca3af', minWidth:100, flexShrink:0 }}>{l}</span>
                    <span style={{ fontSize:12, color:'#111827', fontWeight:500 }}>{v}</span>
                  </div>
                ))}
                {myProfile.about && (
                  <div style={{ marginTop:10 }}>
                    <div style={{ fontSize:11, color:'#9ca3af', marginBottom:4 }}>{t('परिचय','About')}</div>
                    <div style={{ fontSize:12, color:'#374151', lineHeight:1.7 }}>{myProfile.about}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Profile Form ── */}
      {view === 'my_profile_form' && form && (
        <div>
          <button onClick={() => setView(myProfile ? 'my_profile' : 'browse')} style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:12, fontWeight:700, color:'#374151', marginBottom:14, display:'flex', alignItems:'center', gap:4 }}>
            ← {t('वापस','Back')}
          </button>
          <div style={{ background:'#fff', borderRadius:14, border:'1px solid #e5e7eb', padding:'18px', boxShadow:'0 2px 10px rgba(0,0,0,0.07)' }}>
            <div style={{ fontSize:14, fontWeight:800, color:M, marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
              <HeartOutlined /> {myProfile ? t('Profile Update करें','Update Profile') : t('Vivah Profile बनाएं','Create Vivah Profile')}
            </div>

            {/* ── Photo Upload ── */}
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', marginBottom:20, paddingBottom:16, borderBottom:`1px solid ${M}15` }}>
              <div style={{ position:'relative', marginBottom:8 }}>
                {form.photo
                  ? <img src={form.photo} alt="" style={{ width:80, height:80, borderRadius:'50%', objectFit:'cover', objectPosition:'top', border:`3px solid ${M}` }} />
                  : <div style={{ width:80, height:80, borderRadius:'50%', background:`${M}10`, display:'flex', alignItems:'center', justifyContent:'center', border:`2px dashed ${M}50` }}>
                      <CameraOutlined style={{ fontSize:26, color:M }} />
                    </div>
                }
                <label htmlFor="vivah-photo-input" style={{ position:'absolute', bottom:0, right:0, width:26, height:26, borderRadius:'50%', background:uploadingPhoto?'#9ca3af':M, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:12, boxShadow:'0 2px 6px rgba(0,0,0,0.25)' }}>
                  {uploadingPhoto ? <Spin size="small" /> : <CameraOutlined />}
                </label>
                <input id="vivah-photo-input" type="file" accept="image/jpeg,image/png,image/webp" style={{ display:'none' }} onChange={handleVivahPhotoUpload} disabled={uploadingPhoto} />
              </div>
              <div style={{ fontSize:11, color:'#6b7280', marginBottom:6 }}>{t('Profile Photo (वैकल्पिक)','Profile Photo (optional)')}</div>
              <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:11, color:'#374151' }}>
                <input type="checkbox" checked={form.photo_visible === 1} onChange={e => setForm(f => ({...f, photo_visible: e.target.checked ? 1 : 0}))} />
                {t('Photo सभी को दिखाएं','Show photo publicly')}
                <span style={{ fontSize:10, color:'#9ca3af' }}>({t('बंद = केवल Admin देखेंगे','off = admin only')})</span>
              </label>
            </div>

            {/* Form sections */}
            {[
              { title: t('व्यक्तिगत जानकारी','Personal Information'), fields: [
                { col:'1/-1', label:t('Display Name *','Display Name *'), key:'display_name', ph:t('जैसे: रमेश स.','e.g. Ramesh S.') },
                { label:t('लिंग *','Gender *'), key:'gender', type:'select', opts:[['male',t('वर (लड़का)','Groom (Male)')],['female',t('वधू (लड़की)','Bride (Female)')]] },
                { label:t('जन्म तिथि','Date of Birth'), key:'dob', type:'date' },
                { label:t('ऊँचाई (cm)','Height (cm)'), key:'height_cm', type:'number', ph:'e.g. 170' },
                { label:t('वर्ण','Complexion'), key:'complexion', type:'select', opts:[['',''],['very_fair','Very Fair'],['fair','Fair'],['wheatish','Wheatish'],['dark','Dark']] },
                { label:t('वैवाहिक स्थिति','Marital Status'), key:'marital_status', type:'select', opts:[['never_married',t('अविवाहित','Never Married')],['divorced',t('तलाकशुदा','Divorced')],['widowed',t('विधवा/विधुर','Widowed')]] },
              ]},
              { title: t('शिक्षा एवं व्यवसाय','Education & Career'), fields: [
                { label:t('शिक्षा','Education'), key:'education', ph:'B.Com / B.Tech / MBA' },
                { label:t('क्षेत्र','Field'), key:'education_field', ph:'Commerce / Engineering' },
                { label:t('व्यवसाय','Occupation'), key:'occupation', ph:t('CA / Engineer / Farmer','CA / Engineer / Farmer') },
                { label:t('Company/Firm','Company/Firm'), key:'company' },
                { col:'1/-1', label:t('आय','Income Range'), key:'income_range', ph:'₹30,000/month or 5-10 LPA' },
              ]},
              { title: t('वर्तमान स्थान','Current Location'), fields: [
                { label:t('शहर','City'), key:'current_city' },
                { label:t('राज्य','State'), key:'current_state' },
                { label:t('देश','Country'), key:'current_country' },
                { label:'NRI?', key:'is_nri', type:'select', opts:[['0',t('नहीं','No')],['1',t('हाँ (NRI)','Yes (NRI)')]] },
              ]},
              { title: t('सामाजिक जानकारी','Community Details'), fields: [
                { label:t('गोत्र','Gotra'), key:'gotra' },
                { label:t('कुलदेवी','Kuldevi'), key:'kuldevi' },
                { label:t('मूल गाँव','Native Village'), key:'native_village' },
                { label:t('जिला','District'), key:'native_district' },
              ]},
              { title: t('परिवार','Family Details'), fields: [
                { label:t('पिता का नाम','Father Name'), key:'father_name' },
                { label:t('पिता का व्यवसाय','Father Occupation'), key:'father_occupation' },
                { label:t('माता की कुलदेवी','Mother Kuldevi'), key:'mother_kuldevi' },
                { col:'1/-1', label:t('भाई-बहन','Siblings'), key:'siblings', ph:t('e.g. 1 भाई (विवाहित), 1 बहन','e.g. 1 brother (married), 1 sister') },
                { label:t('परिवार का प्रकार','Family Type'), key:'family_type', type:'select', opts:[['',''],['joint',t('संयुक्त','Joint')],['nuclear',t('एकल','Nuclear')]] },
                { label:t('पारिवारिक मूल्य','Family Values'), key:'family_values', type:'select', opts:[['',''],['traditional',t('परंपरागत','Traditional')],['moderate',t('मध्यम','Moderate')],['liberal',t('आधुनिक','Liberal')]] },
              ]},
              { title: t('परिचय एवं प्राथमिकताएँ','About & Preferences'), fields: [
                { col:'1/-1', label:t('अपने बारे में','About Yourself'), key:'about', type:'textarea', ph:t('अपने बारे में 100-300 शब्दों में बताएं…','Tell about yourself in 100-300 words…') },
                { col:'1/-1', label:t('रुचियाँ/शौक','Hobbies / Interests'), key:'hobbies', ph:t('पढ़ना, खेल, संगीत…','Reading, Sports, Music…') },
                { label:t('पसंदीदा आयु (न्यूनतम)','Preferred Age Min'), key:'pref_age_min', type:'number', ph:'20' },
                { label:t('पसंदीदा आयु (अधिकतम)','Preferred Age Max'), key:'pref_age_max', type:'number', ph:'30' },
                { col:'1/-1', label:t('पसंदीदा शहर','Preferred City'), key:'pref_city', ph:'Jaipur, Mumbai, Any' },
                { label:t('NRI स्वीकार?','NRI Acceptable?'), key:'pref_nri_ok', type:'select', opts:[['1',t('हाँ','Yes')],['0',t('नहीं','No')]] },
              ]},
            ].map(section => (
              <div key={section.title} style={{ marginBottom:20 }}>
                <div style={{ fontSize:12, fontWeight:800, color:M, textTransform:'uppercase', letterSpacing:0.5, marginBottom:10, paddingBottom:6, borderBottom:`1px solid ${M}20` }}>{section.title}</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  {section.fields.map(field => (
                    <div key={field.key} style={{ gridColumn: field.col || 'auto' }}>
                      <label style={lStyle}>{field.label}</label>
                      {field.type === 'select' ? (
                        <select style={iStyle} value={form[field.key]??''} onChange={e=>setForm(f=>({...f,[field.key]:field.key==='is_nri'||field.key==='pref_nri_ok'?parseInt(e.target.value):e.target.value}))}>
                          {field.opts.map(([v,l])=><option key={v} value={v}>{l}</option>)}
                        </select>
                      ) : field.type === 'textarea' ? (
                        <textarea style={{ ...iStyle, height:80, resize:'vertical' }} value={form[field.key]||''} onChange={e=>setForm(f=>({...f,[field.key]:e.target.value}))} placeholder={field.ph||''} maxLength={2000}/>
                      ) : (
                        <input type={field.type||'text'} style={iStyle} value={form[field.key]||''} onChange={e=>setForm(f=>({...f,[field.key]:e.target.value}))} placeholder={field.ph||''} maxLength={300}/>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div style={{ display:'flex', gap:10, marginTop:4 }}>
              <button onClick={() => setView(myProfile?'my_profile':'browse')} style={{ flex:1, background:'#fff', color:'#374151', border:'1.5px solid #d1d5db', borderRadius:10, padding:'12px 0', fontWeight:700, fontSize:13, cursor:'pointer' }}>{t('रद्द','Cancel')}</button>
              <button onClick={saveProfile} disabled={submitting} style={{ flex:2, background:submitting?'#9ca3af':M, color:'#fff', border:'none', borderRadius:10, padding:'12px 0', fontWeight:800, fontSize:13, cursor:submitting?'not-allowed':'pointer' }}>
                {submitting ? t('सहेज रहा है…','Saving…') : t('Profile Submit करें ✅','Submit Profile ✅')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Profile Detail + Interest Modal ── */}
      {viewProfile && (
        <div style={{ position:'fixed', inset:0, zIndex:1001, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
          onClick={e => e.target===e.currentTarget && setViewProfile(null)}>
          <div style={{ background:'#fff', borderRadius:18, width:'100%', maxWidth:500, maxHeight:'92vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 60px rgba(0,0,0,0.3)', overflow:'hidden' }}>
            <div style={{ background:`linear-gradient(135deg,${M},#b71c1c)`, padding:'16px 20px', position:'relative', flexShrink:0, display:'flex', gap:14, alignItems:'center' }}>
              {/* Photo or blur in modal header */}
              {viewProfile.photo ? (
                <img src={viewProfile.photo} alt={viewProfile.display_name}
                  style={{ width:64, height:64, borderRadius:'50%', objectFit:'cover', objectPosition:'top', border:'2.5px solid rgba(255,255,255,0.5)', flexShrink:0 }} />
              ) : viewProfile.has_photo ? (
                <BlurPhoto size={64} showLabel />
              ) : (
                <div style={{ width:64, height:64, borderRadius:'50%', background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, fontWeight:900, color:'rgba(255,255,255,0.85)', flexShrink:0, border:'2px solid rgba(255,255,255,0.3)' }}>
                  {viewProfile.display_name?.[0]?.toUpperCase() || '?'}
                </div>
              )}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:16, fontWeight:900, color:'#fff' }}>{viewProfile.display_name}</div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.7)', marginTop:2 }}>
                  {viewProfile.age ? `${viewProfile.age} ${t('वर्ष','yrs')} · ` : ''}{viewProfile.current_city ? toTitle(viewProfile.current_city) : ''}
                </div>
              </div>
              <button onClick={() => setViewProfile(null)} style={{ position:'absolute', top:12, right:14, width:28, height:28, borderRadius:'50%', background:'rgba(255,255,255,0.18)', border:'none', color:'#fff', fontSize:15, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
            </div>
            <div style={{ overflowY:'auto', padding:'16px 18px 8px', flex:1 }}>
              {/* Profile details */}
              {[
                [t('शिक्षा','Education'), [viewProfile.education, viewProfile.education_field].filter(Boolean).join(' · ')],
                [t('व्यवसाय','Occupation'), viewProfile.occupation],
                [t('आय','Income'), viewProfile.income_range],
                [t('स्थान','Location'), [viewProfile.current_city, viewProfile.current_state, viewProfile.is_nri?viewProfile.current_country:null].filter(Boolean).map(toTitle).join(', ')],
                [t('गोत्र','Gotra'), viewProfile.gotra],
                [t('कुलदेवी','Kuldevi'), viewProfile.kuldevi],
                [t('मूल','Native'), [viewProfile.native_village, viewProfile.native_district].filter(Boolean).join(', ')],
                [t('पिता','Father'), viewProfile.father_name ? `${viewProfile.father_name}${viewProfile.father_occupation ? ` (${viewProfile.father_occupation})` : ''}` : null],
                [t('भाई-बहन','Siblings'), viewProfile.siblings],
                [t('परिवार','Family'), viewProfile.family_type === 'joint' ? t('संयुक्त','Joint') : viewProfile.family_type === 'nuclear' ? t('एकल','Nuclear') : null],
              ].filter(([,v])=>v).map(([l,v])=>(
                <div key={l} style={{ display:'flex', gap:12, padding:'7px 0', borderBottom:'1px solid #f3f4f6' }}>
                  <span style={{ fontSize:11, color:'#9ca3af', minWidth:90, flexShrink:0 }}>{l}</span>
                  <span style={{ fontSize:12, color:'#111827', fontWeight:500 }}>{v}</span>
                </div>
              ))}
              {viewProfile.about && <div style={{ marginTop:12, fontSize:12, color:'#374151', lineHeight:1.8 }}>{viewProfile.about}</div>}

              {/* Send Interest section */}
              {!viewProfile.my_interest && (
                <div style={{ marginTop:16 }}>
                  <label style={lStyle}>{t('रुचि संदेश (वैकल्पिक)','Message (optional)')}</label>
                  <textarea style={{ ...iStyle, height:70, resize:'vertical', marginBottom:10 }} value={interestMsg} onChange={e=>setInterestMsg(e.target.value)} placeholder={t('अपने परिवार के बारे में संक्षेप में बताएं…','Brief introduction about your family…')} maxLength={500}/>
                </div>
              )}
            </div>
            {!viewProfile.my_interest ? (
              <div style={{ padding:'12px 18px 16px', borderTop:'1px solid #e5e7eb', display:'flex', gap:10, flexShrink:0 }}>
                <button onClick={() => setViewProfile(null)} style={{ flex:1, background:'#fff', color:'#374151', border:'1.5px solid #d1d5db', borderRadius:10, padding:'11px 0', fontWeight:700, fontSize:13, cursor:'pointer' }}>{t('बंद करें','Close')}</button>
                <button onClick={() => sendInterest(viewProfile.id)} disabled={sending} style={{ flex:2, background:sending?'#9ca3af':M, color:'#fff', border:'none', borderRadius:10, padding:'11px 0', fontWeight:800, fontSize:13, cursor:sending?'not-allowed':'pointer' }}>
                  {sending ? t('भेज रहे हैं…','Sending…') : t('💍 रुचि भेजें','💍 Send Interest')}
                </button>
              </div>
            ) : (
              <div style={{ padding:'12px 18px 16px', borderTop:'1px solid #e5e7eb', textAlign:'center', color:'#6b7280', fontSize:12, flexShrink:0 }}>
                {viewProfile.my_interest==='accepted' ? t('✅ रुचि स्वीकार हो गई — Contact देखें "Sent" tab में','✅ Interest accepted — view contact in Sent tab') :
                 viewProfile.my_interest==='declined' ? t('❌ रुचि अस्वीकार हो गई','❌ Interest declined') :
                 t('⏳ रुचि भेज दी है — Response का इंतजार है','⏳ Interest sent — awaiting response')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Samaj Parichay Tab ───────────────────────────────────────────────────────
const PARICHAY_CAT = {
  identity:   { labelHi: 'हम कौन हैं',  labelEn: 'Who We Are',     Icon: TeamOutlined },
  history:    { labelHi: 'इतिहास',       labelEn: 'History',        Icon: ReadOutlined },
  gotra:      { labelHi: 'गोत्र',        labelEn: 'Gotra',          Icon: AimOutlined },
  kuldevi:    { labelHi: 'कुलदेवी',      labelEn: 'Kuldevi',        Icon: HeartOutlined },
  festivals:  { labelHi: 'त्योहार',      labelEn: 'Festivals',      Icon: StarOutlined },
  language:   { labelHi: 'भाषा',         labelEn: 'Language',       Icon: GlobalOutlined },
  traditions: { labelHi: 'परंपराएँ',     labelEn: 'Traditions',     Icon: BulbOutlined },
  qa:         { labelHi: 'सामान्य प्रश्न', labelEn: 'FAQ',           Icon: QuestionCircleOutlined },
  general:    { labelHi: 'सामान्य',      labelEn: 'General',        Icon: InfoCircleOutlined },
};

export function SamajParichayTab() {
  const { t, lang } = useLang();
  const [items,     setItems]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null);
  const [catFilter, setCatFilter] = useState('all');

  useEffect(() => {
    const CK = 'samaj_parichay';
    const cached = ssGet(CK, 5 * 60 * 1000); // 5-min cache — stale 1-hour cache was causing empty portal
    if (cached && cached.length > 0) { setItems(cached); setLoading(false); return; }
    axios.get(`${baseUrl}/member/samaj-parichay`, { headers: authHeader() })
      .then(r => { if (r.data.status) { setItems(r.data.result || []); ssSet(CK, r.data.result || []); } })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const cats     = [...new Set(items.map(a => a.category))];
  const filtered = catFilter === 'all' ? items : items.filter(a => a.category === catFilter);

  // ── Article / Q&A detail view ──────────────────────────────────────────────
  if (selected) {
    const cm      = PARICHAY_CAT[selected.category] || PARICHAY_CAT.general;
    const CatIcon = cm.Icon;
    const isQA    = selected.type === 'qa';
    const title   = (lang === 'en' && selected.title_en)     ? selected.title_en     : selected.title;
    const question= (lang === 'en' && selected.question_en)  ? selected.question_en  : selected.question;
    const content = (lang === 'en' && selected.content_en)   ? selected.content_en   : selected.content;
    const altLang = lang === 'en'
      ? { label: 'हिंदी में', text: selected.content || selected.question }
      : { label: 'In English', text: selected.content_en || selected.question_en };

    return (
      <div>
        <button onClick={() => setSelected(null)} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8,
          padding: '7px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700,
          color: '#374151', marginBottom: 16,
        }}>
          ← {t('वापस', 'Back')}
        </button>

        <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.09)', border: `1px solid ${M}18` }}>
          {/* Header strip */}
          <div style={{ background: `linear-gradient(135deg, ${M} 0%, #b71c1c 100%)`, padding: '18px 18px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <CatIcon style={{ fontSize: 20, color: '#fff' }} />
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {lang === 'en' ? cm.labelEn : cm.labelHi}
                  {isQA && <span style={{ marginLeft: 6, background: 'rgba(255,255,255,0.2)', borderRadius: 10, padding: '1px 7px' }}>Q&A</span>}
                </div>
                <div style={{ fontSize: 16, fontWeight: 900, color: '#fff', lineHeight: 1.3, marginTop: 3 }}>{title}</div>
              </div>
            </div>
          </div>

          <div style={{ padding: '18px 18px 24px' }}>
            {/* Q&A format */}
            {isQA && question && (
              <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: 10, padding: '12px 14px', marginBottom: 14, display: 'flex', gap: 10 }}>
                <QuestionCircleOutlined style={{ color: '#ca8a04', fontSize: 16, marginTop: 2, flexShrink: 0 }} />
                <div style={{ fontSize: 13, fontWeight: 700, color: '#713f12', lineHeight: 1.7 }}>{question}</div>
              </div>
            )}

            {/* Main content */}
            {content && (
              <div style={{ fontSize: 14, color: '#1f2937', lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>{content}</div>
            )}

            {/* Alternate language */}
            {altLang.text && (
              <div style={{ marginTop: 18, padding: '12px 14px', background: '#fafafa', borderRadius: 10, borderLeft: `3px solid ${M}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: M, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {altLang.label}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.8 }}>{altLang.text}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── List view ──────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header — maroon consistent with portal */}
      <div style={{ background: `linear-gradient(135deg, ${M} 0%, #b71c1c 100%)`, borderRadius: 14, padding: '14px 16px', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <BookOutlined style={{ fontSize: 22, color: '#fff' }} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 900, color: '#fff' }}>{t('समाज परिचय', 'Samaj Parichay')}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
              {t('हमारी संस्कृति, इतिहास और परंपराएँ', 'Our culture, history and traditions')}
            </div>
          </div>
        </div>
      </div>

      {/* NRI note */}
      <div style={{ background: '#fff8f0', border: `1px solid ${M}30`, borderRadius: 10, padding: '10px 14px', marginBottom: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <GlobalOutlined style={{ color: M, fontSize: 16, marginTop: 1, flexShrink: 0 }} />
        <div style={{ fontSize: 11, color: '#374151', lineHeight: 1.6 }}>
          {t(
            'विदेश में रहने वाले राजपुरोहित परिवारों के बच्चों के लिए — अपनी जड़ों को जानें।',
            'For Rajpurohit families abroad — all articles and Q&As are available in English to help the next generation connect with their roots.'
          )}
        </div>
      </div>

      {/* Category filter */}
      {cats.length > 1 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {['all', ...cats].map(cat => {
            const active = catFilter === cat;
            const cm = PARICHAY_CAT[cat] || {};
            const label = cat === 'all'
              ? t('सभी', 'All')
              : (lang === 'en' ? cm.labelEn : cm.labelHi) || cat;
            return (
              <button key={cat} onClick={() => setCatFilter(cat)} style={{
                fontSize: 11, fontWeight: 700, padding: '5px 13px', borderRadius: 20, cursor: 'pointer',
                background: active ? M : '#f9fafb',
                color: active ? '#fff' : '#374151',
                border: `1px solid ${active ? M : '#e5e7eb'}`,
              }}>{label}</button>
            );
          })}
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>}

      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <BookOutlined style={{ fontSize: 36, color: '#d1d5db', display: 'block', margin: '0 auto 10px' }} />
          <div style={{ fontSize: 13, color: '#9ca3af' }}>{t('अभी कोई सामग्री उपलब्ध नहीं है।', 'No content available yet.')}</div>
          <div style={{ fontSize: 11, color: '#d1d5db', marginTop: 4 }}>{t('Admin से संपर्क करें।', 'Please contact the admin.')}</div>
        </div>
      )}

      {!loading && filtered.map(item => {
        const cm      = PARICHAY_CAT[item.category] || PARICHAY_CAT.general;
        const CatIcon = cm.Icon;
        const isQA    = item.type === 'qa';
        const title   = (lang === 'en' && item.title_en)   ? item.title_en   : item.title;
        const preview = ((lang === 'en' && item.content_en) ? item.content_en : item.content || '').slice(0, 90);
        const hasEn   = !!(item.title_en || item.content_en || item.question_en);

        return (
          <div key={item.id} onClick={() => setSelected(item)} style={{
            background: '#fff', borderRadius: 12, marginBottom: 10,
            border: '1px solid #e5e7eb', cursor: 'pointer',
            display: 'flex', alignItems: 'flex-start', gap: 0,
            boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }}>
            {/* Left accent bar + icon */}
            <div style={{ width: 54, flexShrink: 0, background: `${M}0d`, borderRight: `3px solid ${M}`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 0' }}>
              <CatIcon style={{ fontSize: 20, color: M }} />
            </div>
            {/* Content */}
            <div style={{ flex: 1, minWidth: 0, padding: '12px 13px 12px 13px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: M, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {lang === 'en' ? cm.labelEn : cm.labelHi}
                </span>
                {isQA && (
                  <span style={{ fontSize: 9, fontWeight: 700, background: `${M}15`, color: M, borderRadius: 20, padding: '1px 7px', border: `1px solid ${M}30` }}>
                    Q&A
                  </span>
                )}
                {hasEn && (
                  <span style={{ fontSize: 9, fontWeight: 700, background: '#dbeafe', color: '#1e40af', borderRadius: 20, padding: '1px 7px' }}>
                    EN
                  </span>
                )}
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#111827', lineHeight: 1.35, marginBottom: preview ? 5 : 0 }}>{title}</div>
              {preview && <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.6 }}>{preview}{preview.length >= 90 ? '…' : ''}</div>}
            </div>
            {/* Arrow */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', color: '#d1d5db', fontSize: 16, flexShrink: 0, alignSelf: 'center' }}>›</div>
          </div>
        );
      })}
    </div>
  );
}


// ── Push Notification registration (used by SamajParichayTab) ───────────────
export async function registerPushSubscription() {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    const reg = await navigator.serviceWorker.register('/push-sw.js');
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;
    // Get VAPID public key
    const vRes = await axios.get(`${baseUrl}/member/push/vapid-key`, { headers: authHeader() });
    if (!vRes.data.status) return;
    const vapidPublicKey = vRes.data.publicKey;
    // Convert VAPID key to Uint8Array
    const converted = Uint8Array.from(atob(vapidPublicKey.replace(/-/g,'+').replace(/_/g,'/')), c => c.charCodeAt(0));
    // Subscribe
    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: converted });
    // Send to server
    await axios.post(`${baseUrl}/member/push/subscribe`, sub.toJSON(), { headers: authHeader() });
  } catch {}
}

// ── Samaj Parichay Tab ────────────────────────────────────────────────────────
